import { useEffect } from 'react';

import { fitBounds } from '../../../modules/referenceView/Tkv2MlsMap/fitBounds.ts';
import MapBox from '../../map/MapBox.ts';
import { createCircleCoordinates } from './createCircleCoordinates.ts';

const CIRCLE_POINTS = 64; // Default number of points for circle

// Manages the radius circle on the map based on the geoSearch type and radius
export const useRadiusCircle = ({
  mapRef,
  subjectLat,
  subjectLng,
  geoSearch,
  mapLoaded,
}: {
  mapRef: React.RefObject<MapBox>;
  subjectLat: number;
  subjectLng: number;
  geoSearch: { t: string; radiusInMeters?: number };
  mapLoaded: boolean;
}) => {
  useEffect(() => {
    const mapBox = mapRef.current;
    const mapTarget = mapBox?.getMap();
    if (!mapBox || !mapLoaded || !mapTarget) return;
    const source = mapTarget.getSource('circle-source');
    if (geoSearch.t === 'radius') {
      const draw = mapBox.getDraw();
      if (mapTarget.hasControl(draw as any)) {
        draw.changeMode('simple_select');
        mapBox.disablePolygonDrawButton();
        mapBox.disableTrashButton();
      }
      if (typeof geoSearch.radiusInMeters === 'number') {
        const circleCoordinates = createCircleCoordinates(
          { latitude: subjectLat, longitude: subjectLng },
          geoSearch.radiusInMeters,
          CIRCLE_POINTS, // Default points for circle
        );

        fitBounds(mapTarget, [
          { latitude: subjectLat, longitude: subjectLng },
          ...circleCoordinates.map(([circleLon, circleLat]) => ({
            latitude: circleLat,
            longitude: circleLon,
          })),
        ]);

        if (source && source.type === 'geojson') {
          source?.setData({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [circleCoordinates],
                },
                properties: {},
              },
            ],
          });
        } else {
          mapRef?.current?.getMap().addSource('circle-source', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: {
                    type: 'Polygon',
                    coordinates: [circleCoordinates],
                  },
                  properties: {},
                },
              ],
            },
          });

          mapRef?.current?.getMap().addLayer({
            id: 'circle-polygon',
            type: 'fill',
            source: 'circle-source',
            layout: {},
            paint: {
              'fill-color': '#EE9611',
              'fill-opacity': 0.1,
            },
          });

          mapRef?.current?.getMap().addLayer({
            id: 'circle-outline',
            type: 'line',
            source: 'circle-source',
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': '#EE9611',
              'line-width': 2,
            },
          });
        }
      }
    } else if (source && source.type === 'geojson') {
      source?.setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }, [geoSearch, mapLoaded, geoSearch.radiusInMeters]);
};
